import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'

import { type AppTimezonePreference, getTimezonePreferenceQueryKeyPart } from '@sparkai/utils'

import { apiClient } from '../apiClient'
import { getSelectedTimezoneHeaders } from '../timezoneRequestContext'

type TrainingModuleStats = {
  programId: string
  color: string
  icon?: string
  name: string
  displayName: string
  operatorName?: string
  trainingSessionApi?: 'legacy' | 'modern'
  description: string
  trainingModules: {
    trainingModuleId: string
    title: string
    order: number
    targetTtc?: string
    targetAccuracy?: number
    attempts: number
    approvedAttempts: number
    avgAccuracy: number | null
    approvedOnTraining: boolean
    avgTtc: number | null
    bestAccuracy?: number
    taskAmount: number | null
    hasTrainingMaterial?: boolean
  }[]
  trainingStart: string | null
  trainingEnd: string | null
  trainingState: 'in-progress' | 'not-started' | 'finished'
  progress: number
  avgAccuracy?: number | null
  latestSopUpdate: string | null
  avgTtc?: number | null
  programInstance?: {
    programInstanceId: string
    name: string | null
    displayName: string | null
    msDisplayName: string | null
    programGroup: {
      programGroupId: string
      name: string
      displayName: string | null
    }
    sourcePrograms?: {
      liveProgramId: string
      recyclerProgramId: string | null
    }
  } | null
}

type MineTrainingStatsOptions = {
  enabled?: boolean
  refetchOnMount?: boolean | 'always'
}

export const useGetMineTrainingStats = ({
  enabled = true,
  refetchOnMount = true,
}: MineTrainingStatsOptions = {}) => {
  return useQuery({
    queryKey: ['training-stats-mine'],
    queryFn: async () => {
      const { data } = await apiClient.get<TrainingModuleStats[]>('int/v3/operators/training-stats/mine')
      return data
    },
    enabled,
    refetchOnMount,
  })
}

export const useInvalidateMineTrainingStats = () => {
  const queryClient = useQueryClient()
  return useCallback(
    () => queryClient.invalidateQueries({ queryKey: ['training-stats-mine'] }),
    [queryClient]
  )
}

export const useGetTrainingStats = (operatorId?: string, timezonePreference?: AppTimezonePreference) => {
  return useQuery({
    queryKey: timezonePreference
      ? ['training-stats', operatorId, getTimezonePreferenceQueryKeyPart(timezonePreference)]
      : ['training-stats', operatorId],
    queryFn: async () => {
      const url = `int/v3/operators/training-stats/${operatorId}`
      const { data } = timezonePreference
        ? await apiClient.get<TrainingModuleStats[]>(url, {
            headers: getSelectedTimezoneHeaders(timezonePreference),
          })
        : await apiClient.get<TrainingModuleStats[]>(url)
      return data
    },
    refetchOnWindowFocus: false,
    enabled: Boolean(operatorId),
  })
}
